import { useState, useEffect } from "react";
import { Briefcase, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const JobQuotaBadge = ({ token, quota, onQuotaUpdate, onBuyClick }) => {
  const [loading, setLoading] = useState(false);

  const fetchQuota = async () => {
    if (!token) return;
    
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/payments/quota`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      if (res.ok) {
        const data = await res.json();
        if (onQuotaUpdate) {
          onQuotaUpdate(data);
        }
      }
    } catch (err) {
      console.error("Failed to fetch quota:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token && !quota) {
      fetchQuota();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  if (!quota) return null;

  const usagePercent = Math.min(100, (quota.jobs_used / quota.jobs_limit) * 100);
  const isAtLimit = quota.is_at_limit;
  const isNearLimit = usagePercent >= 80;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className={`flex items-center gap-2 px-3 py-1.5 rounded-full ${
              isAtLimit
                ? "bg-red-50 text-red-700 hover:bg-red-100"
                : isNearLimit
                ? "bg-amber-50 text-amber-700 hover:bg-amber-100"
                : "bg-slate-100 text-slate-600 hover:bg-slate-200"
            }`}
            onClick={isAtLimit ? onBuyClick : undefined}
            data-testid="job-quota-badge"
          >
            {isAtLimit ? (
              <AlertTriangle className="h-4 w-4" />
            ) : (
              <Briefcase className="h-4 w-4" />
            )}
            <span className="text-sm font-medium">
              {quota.jobs_used} / {quota.jobs_limit}
            </span>
          </Button>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="max-w-xs">
          <div className="space-y-2 p-1">
            <p className="font-medium">
              {isAtLimit
                ? "Job limit reached! Click to buy more credits."
                : isNearLimit
                ? "You're running low on job slots."
                : "Jobs tracked"}
            </p>
            <div className="h-1.5 bg-slate-200 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all ${
                  isAtLimit
                    ? "bg-red-500"
                    : isNearLimit
                    ? "bg-amber-500"
                    : "bg-[#4A728D]"
                }`}
                style={{ width: `${usagePercent}%` }}
              />
            </div>
            <p className="text-xs text-slate-500">
              {quota.jobs_limit - quota.jobs_used} slots remaining
            </p>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export default JobQuotaBadge;
